import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { visitsAPI, criteriaAPI, assessmentsAPI, imagesAPI } from '../services/api';
import './AssessmentForm.css';

function AssessmentForm() {
  const navigate = useNavigate();
  const { visitId } = useParams();

  const [visit, setVisit] = useState(null);
  const [criteria, setCriteria] = useState([]);
  const [assessments, setAssessments] = useState({});
  const [images, setImages] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [currentCategory, setCurrentCategory] = useState(null);

  useEffect(() => {
    fetchData();
  }, [visitId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch visit details
      const visitResponse = await visitsAPI.getById(visitId);
      setVisit(visitResponse.data.data);

      // Fetch criteria with existing assessments
      const assessmentsResponse = await assessmentsAPI.getByVisit(visitId);
      const criteriaData = assessmentsResponse.data.data;
      
      setCriteria(criteriaData);

      // Initialize assessments state
      const assessmentsMap = {};
      criteriaData.forEach((criterion) => {
        assessmentsMap[criterion.criterion_id] = {
          compliance_status: criterion.compliance_status || '',
          observations: criterion.observations || '',
          action_required: criterion.action_required || '',
          sub_assessments: {},
        };

        // Initialize sub-assessments
        if (criterion.sub_criteria) {
          criterion.sub_criteria.forEach((sub) => {
            assessmentsMap[criterion.criterion_id].sub_assessments[sub.sub_criterion_id] = {
              compliance_status: sub.compliance_status || '',
              observations: sub.observations || '',
            };
          });
        }
      });
      
      setAssessments(assessmentsMap);

      // Set first category as current
      if (criteriaData.length > 0) {
        setCurrentCategory(criteriaData[0].category);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      alert('Failed to load assessment data');
    } finally {
      setLoading(false);
    }
  };

  const handleCriterionChange = (criterionId, field, value) => {
    setAssessments((prev) => ({
      ...prev,
      [criterionId]: {
        ...prev[criterionId],
        [field]: value,
      },
    }));
  };

  const handleSubCriterionChange = (criterionId, subCriterionId, field, value) => {
    setAssessments((prev) => ({
      ...prev,
      [criterionId]: {
        ...prev[criterionId],
        sub_assessments: {
          ...prev[criterionId].sub_assessments,
          [subCriterionId]: {
            ...prev[criterionId].sub_assessments[subCriterionId],
            [field]: value,
          },
        },
      },
    }));
  };

  const handleImageUpload = async (criterionId, files) => {
    try {
      // Upload images with just visit_id (no assessment_id required initially)
      const formData = new FormData();
      formData.append('visit_id', visitId);
      // Don't send assessment_id - images will be linked to visit only

      Array.from(files).forEach((file) => {
        formData.append('images', file);
      });

      const response = await imagesAPI.uploadMultiple(formData);
      
      // Refresh images
      setImages((prev) => ({
        ...prev,
        [criterionId]: [
          ...(prev[criterionId] || []),
          ...response.data.data,
        ],
      }));

      alert(`${files.length} image(s) uploaded successfully`);
    } catch (err) {
      console.error('Error uploading images:', err);
      const errorMessage = err.response?.data?.message || 'Failed to upload images';
      alert(errorMessage);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Prepare assessments data - only include assessments with a status
      const assessmentsData = Object.keys(assessments)
        .filter((criterionId) => {
          const assessment = assessments[criterionId];
          // Only include if compliance_status is set
          return assessment.compliance_status && assessment.compliance_status.trim() !== '';
        })
        .map((criterionId) => {
          const assessment = assessments[criterionId];
          
          return {
            criterion_id: parseInt(criterionId),
            compliance_status: assessment.compliance_status.trim(),
            observations: assessment.observations || '',
            action_required: assessment.action_required || '',
            sub_assessments: Object.keys(assessment.sub_assessments)
              .filter((subId) => {
                // Only include sub-assessments with a status
                const subAssessment = assessment.sub_assessments[subId];
                return subAssessment.compliance_status && subAssessment.compliance_status.trim() !== '';
              })
              .map((subId) => ({
                sub_criterion_id: parseInt(subId),
                compliance_status: assessment.sub_assessments[subId].compliance_status.trim(),
                observations: assessment.sub_assessments[subId].observations || '',
              })),
          };
        });

      if (assessmentsData.length === 0) {
        alert('Please assess at least one criterion before saving.');
        setSaving(false);
        return;
      }

      await assessmentsAPI.batchSave({
        visit_id: parseInt(visitId),
        assessments: assessmentsData,
      });

      alert('Assessment saved successfully!');
      navigate(`/visits/${visitId}`);
    } catch (err) {
      console.error('Error saving assessment:', err);
      alert('Failed to save assessment. Please check all required fields.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="loading">Loading assessment...</div>;

  const categories = [...new Set(criteria.map((c) => c.category))];
  const currentCriteria = criteria.filter((c) => c.category === currentCategory);

  return (
    <div className="assessment-container">
      <div className="assessment-header">
        <div>
          <h2>Site Visit Assessment</h2>
          {visit && (
            <p className="visit-info">
              {visit.project_name} - {new Date(visit.visit_date).toLocaleDateString()}
            </p>
          )}
        </div>
        <button onClick={handleSave} disabled={saving} className="btn btn-primary">
          {saving ? 'Saving...' : 'Save Assessment'}
        </button>
      </div>

      <div className="assessment-body">
        {/* Category Tabs */}
        <div className="category-tabs">
          {categories.map((category) => (
            <button
              key={category}
              className={`tab ${currentCategory === category ? 'active' : ''}`}
              onClick={() => setCurrentCategory(category)}
            >
              {category}
            </button>
          ))}
        </div>

        {/* Criteria Assessment Forms */}
        <div className="criteria-list">
          {currentCriteria.map((criterion) => (
            <div key={criterion.criterion_id} className="criterion-card">
              <div className="criterion-header">
                <h3>
                  {criterion.criterion_code}: {criterion.criterion_name}
                  {criterion.criterion_mandatory && (
                    <span className="mandatory-badge">MANDATORY</span>
                  )}
                </h3>
              </div>

              {/* Sub-criteria */}
              {criterion.sub_criteria && criterion.sub_criteria.length > 0 && (
                <div className="sub-criteria-section">
                  {criterion.sub_criteria.map((sub) => (
                    <div key={sub.sub_criterion_id} className="sub-criterion">
                      <div className="sub-criterion-header">
                        <p>
                          {sub.sub_criterion_text}
                          {sub.is_mandatory && (
                            <span className="mandatory-label">(Mandatory)</span>
                          )}
                        </p>
                      </div>

                      <div className="form-row">
                        <div className="form-group">
                          <label>Compliance Status</label>
                          <select
                            value={
                              assessments[criterion.criterion_id]?.sub_assessments?.[
                                sub.sub_criterion_id
                              ]?.compliance_status || ''
                            }
                            onChange={(e) =>
                              handleSubCriterionChange(
                                criterion.criterion_id,
                                sub.sub_criterion_id,
                                'compliance_status',
                                e.target.value
                              )
                            }
                          >
                            <option value="">Select status</option>
                            <option value="YES">Yes</option>
                            <option value="NO">No</option>
                            <option value="PARTLY">Partly</option>
                            <option value="TO BE VERIFIED">To Be Verified</option>
                          </select>
                        </div>

                        <div className="form-group full-width">
                          <label>Observations</label>
                          <textarea
                            value={
                              assessments[criterion.criterion_id]?.sub_assessments?.[
                                sub.sub_criterion_id
                              ]?.observations || ''
                            }
                            onChange={(e) =>
                              handleSubCriterionChange(
                                criterion.criterion_id,
                                sub.sub_criterion_id,
                                'observations',
                                e.target.value
                              )
                            }
                            rows="3"
                            placeholder="Enter observations..."
                          />
                        </div>
                      </div>

                      {/* Show action template if non-compliant */}
                      {sub.action_template &&
                        ['NO', 'PARTLY'].includes(
                          assessments[criterion.criterion_id]?.sub_assessments?.[
                            sub.sub_criterion_id
                          ]?.compliance_status
                        ) && (
                          <div className="action-template">
                            <strong>Action to be taken:</strong>
                            <p>{sub.action_template}</p>
                          </div>
                        )}
                    </div>
                  ))}
                </div>
              )}

              {/* Overall criterion assessment */}
              <div className="criterion-assessment">
                <div className="form-row">
                  <div className="form-group">
                    <label>Overall Compliance</label>
                    <select
                      value={assessments[criterion.criterion_id]?.compliance_status || ''}
                      onChange={(e) =>
                        handleCriterionChange(
                          criterion.criterion_id,
                          'compliance_status',
                          e.target.value
                        )
                      }
                    >
                      <option value="">Select status</option>
                      <option value="YES">Yes</option>
                      <option value="NO">No</option>
                      <option value="PARTLY">Partly</option>
                      <option value="TO BE VERIFIED">To Be Verified</option>
                    </select>
                  </div>

                  <div className="form-group full-width">
                    <label>Overall Observations</label>
                    <textarea
                      value={assessments[criterion.criterion_id]?.observations || ''}
                      onChange={(e) =>
                        handleCriterionChange(
                          criterion.criterion_id,
                          'observations',
                          e.target.value
                        )
                      }
                      rows="4"
                      placeholder="Enter overall observations..."
                    />
                  </div>

                  <div className="form-group full-width">
                    <label>Action Required</label>
                    <textarea
                      value={assessments[criterion.criterion_id]?.action_required || ''}
                      onChange={(e) =>
                        handleCriterionChange(
                          criterion.criterion_id,
                          'action_required',
                          e.target.value
                        )
                      }
                      rows="3"
                      placeholder="Specify any action required..."
                    />
                  </div>
                </div>

                {/* Image Upload */}
                <div className="image-upload-section">
                  <label>Reference Images</label>
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={(e) =>
                      handleImageUpload(criterion.criterion_id, e.target.files)
                    }
                  />
                  {images[criterion.criterion_id] && (
                    <div className="uploaded-images">
                      {images[criterion.criterion_id].map((img) => (
                        <div key={img.image_id} className="image-preview">
                          <img
                            src={imagesAPI.getImageUrl(img.image_id)}
                            alt={`Image ${img.image_number}`}
                          />
                          <span>Image {img.image_number}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="assessment-footer">
        <button onClick={() => navigate(`/visits/${visitId}`)} className="btn btn-secondary">
          Cancel
        </button>
        <button onClick={handleSave} disabled={saving} className="btn btn-primary">
          {saving ? 'Saving...' : 'Save Assessment'}
        </button>
      </div>
    </div>
  );
}

export default AssessmentForm;